import cardValidator from 'card-validator'
import { analytics } from 'payment-widget-core'

/**
 * Воронка конверсии платёжной формы.
 *
 * Тонкая обёртка над analytics из payment-widget-core: собирает
 * безопасные payload'ы (PAN маскируется) и шлёт события этапов.
 * Если аналитика не активна (нет активного провайдера) — все вызовы
 * no-op, т.к. фасад сам это гасит.
 *
 * Этапы воронки:
 *   form_initialized      — форма проинициализирована (ответ /form/init)
 *   card_entered          — введён валидный номер карты
 *   field_filled          — заполнено поле (fio / cvc)
 *   form_sale_pre_request  — перед отправкой /form/pay
 *   form_sale_response    — получен ответ на /form/pay
 *   form_left             — пользователь покинул форму (sendBeacon)
 */

/** Маска PAN: первые 6 и последние 4, середина скрыта. */
export function maskPan(pan) {
  const digits = String(pan ?? '').replace(/\D/g, '')
  if (digits.length < 10) {
    return digits ? `${digits.slice(0, 2)}***` : null
  }
  const head = digits.slice(0, 6)
  const tail = digits.slice(-4)
  const middle = '*'.repeat(digits.length - 10)
  return `${head}${middle}${tail}`
}

/** Кастомные BIN'ы, которых нет в card-validator (Humo / Uzcard). */
function localBrand(digits) {
  if (/^9860/.test(digits)) return 'humo'
  if (/^(8600|5614)/.test(digits)) return 'uzcard'
  return null
}

/** Бренд + валидность номера карты. */
export function panMeta(pan) {
  const digits = String(pan ?? '').replace(/\D/g, '')
  let brand = null
  let valid = false
  try {
    const result = cardValidator.number(digits)
    brand = result.card?.type ?? null      // 'visa' | 'mastercard' | ...
    valid = !!result.isValid
  } catch {
    // card-validator упал — оставляем дефолты
  }
  return {
    pan_masked: maskPan(digits),
    brand: localBrand(digits) ?? brand,
    valid,
  }
}

/**
 * Секунды от старта загрузки страницы (performance.now() отсчитывается от
 * timeOrigin — навигации). Кладём в каждое событие как `t`, чтобы раскладывать
 * события на таймлайн: form_initialized ~0.5, card_entered ~3.2 и т.д.
 */
function sincePageStart() {
  const ms =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now() - (sincePageStart._start ?? (sincePageStart._start = Date.now()))
  return Math.round(ms / 100) / 10 // секунды, 1 знак после запятой
}

/** Отправить событие воронки с относительным временем `t` (сек от старта страницы). */
function emit(name, payload = {}) {
  analytics.track(name, { t: sincePageStart(), ...payload })
}

export const funnel = {
  /** 1) Форма проинициализирована. */
  formInitialized(payload = {}) {
    emit('form_initialized', payload)
  },

  /** 2) Введён номер карты. */
  cardEntered(pan, payload = {}) {
    emit('card_entered', { ...panMeta(pan), ...payload })
  },

  /** Факт заполнения поля: 'fio' | 'cvc' | 'date'. */
  fieldFilled(field, payload = {}) {
    emit('field_filled', { field, ...payload })
  },

  /** 3) Перед запросом /form/pay (form/sale). */
  formSalePreRequest(pan, payload = {}) {
    emit('form_sale_pre_request', { ...panMeta(pan), ...payload })
  },

  /** 3.1) Ответ на /form/pay: результат запроса (успех/ошибка/сеть). */
  formSaleResponse(payload = {}) {
    emit('form_sale_response', payload)
  },

  /** 4) Покинул форму — отправляется через sendBeacon на выгрузке. */
  formLeft(payload = {}) {
    emit('form_left', payload)
    analytics.flush()
  },
}

/**
 * Навесить отслеживание ухода со страницы.
 * pagehide / visibilitychange:hidden — надёжнее beforeunload на mobile.
 * Вызывать один раз при старте приложения.
 */
export function attachLeaveTracking() {
  // let sent = false
  // const onLeave = () => {
  //   if (sent) return
  //   sent = true
  //   funnel.formLeft()
  // }
  // window.addEventListener('pagehide', onLeave)
  // document.addEventListener('visibilitychange', () => {
  //   if (document.visibilityState === 'hidden') onLeave()
  // })
}
